import { HttpClient, HttpParams } from "@angular/common/http";
import { inject, Injectable } from "@angular/core";

import {
  BusinessUnit,
  OrderListResponse,
  OrderQueryParams,
  OrderStatsResponse,
} from "@shared/models/order.models";
import { Observable, shareReplay } from "rxjs";

import {
  AssignableUser,
  BOARD_VIEW_MODE,
  BoardResponse,
  BoardViewMode,
  OwnedCountResponse,
  PaymentBoardResponse,
  SelectableStatus,
} from "@juristech/contracts/orders/board.types";

import { OWNER_UNASSIGNED } from "../state/dashboard-filters.store";

import { ApiConfig } from "./api.config";

/** A single owner board-filter token: a user id or the `unassigned` sentinel. */
export type BoardOwnerParam = number | typeof OWNER_UNASSIGNED;

export interface BoardQueryParams {
  search?: string;
  orderStatusId?: number;
  businessUnitIds?: number[];
  paymentStatus?: "paid" | "partial" | "unpaid";
  companyId?: number;
  /** Property-state filter (`[Location].[States].Id`). */
  stateId?: number;
  /** Property-county filter (`[Location].[Counties].Id`), sent comma-joined. */
  countyIds?: number[];
  /** ISO-8601 string */
  dateFrom?: string;
  /** ISO-8601 string */
  dateTo?: string;
  /**
   * Board owner filter passed to `?owner=` as a comma-joined list (e.g.
   * `4088,unassigned`). The board narrows to orders owned by ANY token: a numeric
   * id matches that owner, `'unassigned'` matches ownerless orders. Empty/omitted
   * applies no owner filter.
   */
  owner?: BoardOwnerParam[];
  /**
   * Board lane selector. `'payment'` requests the dunning supervision board;
   * absent/`'urgency'` is the SLA board. `getPaymentBoardOrders` pins this to
   * `payment`, so it is informational on the shared filter shape.
   */
  viewMode?: BoardViewMode;
}

@Injectable({ providedIn: "root" })
export class OrdersService {
  private http = inject(HttpClient);
  private api = inject(ApiConfig);

  /**
   * Cached `GET /orders/assignable-users` stream. Lazily created on first
   * {@link getAssignableUsers} call and `shareReplay(1)`-multicast so every
   * board card shares one fetch (and replays the result to late subscribers)
   * rather than each kebab popover issuing its own request.
   */
  private assignableUsers$: Observable<AssignableUser[]> | null = null;

  getOrders(params: OrderQueryParams): Observable<OrderListResponse> {
    let httpParams = new HttpParams();

    if (params.page != null) httpParams = httpParams.set("page", params.page);
    if (params.limit != null)
      httpParams = httpParams.set("limit", params.limit);
    if (params.search) httpParams = httpParams.set("search", params.search);
    if (params.orderStatusId != null)
      httpParams = httpParams.set("orderStatusId", params.orderStatusId);
    if (params.businessUnitIds?.length)
      httpParams = httpParams.set(
        "businessUnitIds",
        params.businessUnitIds.join(","),
      );
    if (params.paymentStatus)
      httpParams = httpParams.set("paymentStatus", params.paymentStatus);
    if (params.dateFrom)
      httpParams = httpParams.set("dateFrom", params.dateFrom);
    if (params.dateTo) httpParams = httpParams.set("dateTo", params.dateTo);
    if (params.companyId != null)
      httpParams = httpParams.set("companyId", params.companyId);
    if (params.stateId != null)
      httpParams = httpParams.set("stateId", params.stateId);
    if (params.countyIds?.length)
      httpParams = httpParams.set("countyIds", params.countyIds.join(","));
    if (params.assignedToUserId != null)
      httpParams = httpParams.set("assignedToUserId", params.assignedToUserId);
    if (params.owner?.length)
      httpParams = httpParams.set(
        "owner",
        params.owner.map((token) => String(token)).join(","),
      );
    if (params.isCompleted !== undefined)
      httpParams = httpParams.set(
        "isCompleted",
        params.isCompleted ? "true" : "false",
      );
    if (params.sortBy) httpParams = httpParams.set("sortBy", params.sortBy);
    if (params.sortOrder)
      httpParams = httpParams.set("sortOrder", params.sortOrder);

    return this.http.get<OrderListResponse>(this.api.buildUrl("orders"), {
      params: httpParams,
    });
  }

  /**
   * Fetches the kanban-board view (`GET /orders/board`).
   *
   * @param params - Optional filter set; mirrors `getOrders` minus pagination/sort.
   * @returns Server-bucketed `{ critical, rush, standard, counts }` response.
   */
  getBoardOrders(params: BoardQueryParams): Observable<BoardResponse> {
    let httpParams = new HttpParams();
    if (params.search) httpParams = httpParams.set("search", params.search);
    if (params.orderStatusId != null)
      httpParams = httpParams.set("orderStatusId", params.orderStatusId);
    if (params.businessUnitIds?.length)
      httpParams = httpParams.set(
        "businessUnitIds",
        params.businessUnitIds.join(","),
      );
    if (params.paymentStatus)
      httpParams = httpParams.set("paymentStatus", params.paymentStatus);
    if (params.companyId != null)
      httpParams = httpParams.set("companyId", params.companyId);
    if (params.stateId != null)
      httpParams = httpParams.set("stateId", params.stateId);
    if (params.countyIds?.length)
      httpParams = httpParams.set("countyIds", params.countyIds.join(","));
    if (params.dateFrom)
      httpParams = httpParams.set("dateFrom", params.dateFrom);
    if (params.dateTo) httpParams = httpParams.set("dateTo", params.dateTo);
    if (params.owner?.length)
      httpParams = httpParams.set(
        "owner",
        params.owner.map((token) => String(token)).join(","),
      );

    return this.http.get<BoardResponse>(this.api.buildUrl("orders/board"), {
      params: httpParams,
    });
  }

  /**
   * Fetches the payment-supervision board (`GET /orders/board?viewMode=payment`).
   *
   * Hits the same route as {@link getBoardOrders}; slice-11's controller branches
   * on `viewMode=payment` to return the 5 payment lanes. Forwards the identical
   * filter set so payment-mode filtering matches urgency-mode filtering.
   *
   * @param params - Optional filter set; the same shape as {@link getBoardOrders}.
   * @returns The 5-lane `{ needsAttention, unpaid, linkSent, partial, paid, counts }`.
   */
  getPaymentBoardOrders(
    params: BoardQueryParams,
  ): Observable<PaymentBoardResponse> {
    let httpParams = new HttpParams().set("viewMode", BOARD_VIEW_MODE.Payment);
    if (params.search) httpParams = httpParams.set("search", params.search);
    if (params.orderStatusId != null)
      httpParams = httpParams.set("orderStatusId", params.orderStatusId);
    if (params.businessUnitIds?.length)
      httpParams = httpParams.set(
        "businessUnitIds",
        params.businessUnitIds.join(","),
      );
    if (params.paymentStatus)
      httpParams = httpParams.set("paymentStatus", params.paymentStatus);
    if (params.companyId != null)
      httpParams = httpParams.set("companyId", params.companyId);
    if (params.stateId != null)
      httpParams = httpParams.set("stateId", params.stateId);
    if (params.countyIds?.length)
      httpParams = httpParams.set("countyIds", params.countyIds.join(","));
    if (params.dateFrom)
      httpParams = httpParams.set("dateFrom", params.dateFrom);
    if (params.dateTo) httpParams = httpParams.set("dateTo", params.dateTo);
    if (params.owner?.length)
      httpParams = httpParams.set(
        "owner",
        params.owner.map((token) => String(token)).join(","),
      );

    return this.http.get<PaymentBoardResponse>(
      this.api.buildUrl("orders/board"),
      { params: httpParams },
    );
  }

  /**
   * Pauses the dunning ladder for an order (`POST …/orders/:orderId/pause`) —
   * the order stays visible but is excluded from due-work until resumed.
   *
   * @param orderId - The order whose dunning row to pause.
   * @returns The server's state-flip acknowledgement (not modelled by callers).
   */
  pauseDunning(orderId: number): Observable<unknown> {
    return this.http.post(
      this.api.buildUrl(`payments/dunning/orders/${orderId}/pause`),
      {},
    );
  }

  /**
   * Resumes a paused dunning ladder (`POST …/orders/:orderId/resume`).
   *
   * @param orderId - The order whose dunning row to resume.
   * @returns The server's state-flip acknowledgement.
   */
  resumeDunning(orderId: number): Observable<unknown> {
    return this.http.post(
      this.api.buildUrl(`payments/dunning/orders/${orderId}/resume`),
      {},
    );
  }

  /**
   * Re-arms a stalled/exhausted ladder for another reminder pass
   * (`POST …/orders/:orderId/re-arm` — note the hyphenated URL segment).
   *
   * @param orderId - The order whose dunning row to re-arm.
   * @returns The server's state-flip acknowledgement.
   */
  reArmDunning(orderId: number): Observable<unknown> {
    return this.http.post(
      this.api.buildUrl(`payments/dunning/orders/${orderId}/re-arm`),
      {},
    );
  }

  /**
   * Abandons an order's dunning ladder (`POST …/orders/:orderId/abandon`) — a
   * rare/HITL terminal state; the card stays visible with a manual retry.
   *
   * @param orderId - The order whose dunning row to abandon.
   * @returns The server's state-flip acknowledgement.
   */
  abandonDunning(orderId: number): Observable<unknown> {
    return this.http.post(
      this.api.buildUrl(`payments/dunning/orders/${orderId}/abandon`),
      {},
    );
  }

  /**
   * Retries an abandoned order's dunning (`POST …/orders/:orderId/retry`),
   * returning it to the active ladder.
   *
   * @param orderId - The order whose dunning row to retry.
   * @returns The server's state-flip acknowledgement.
   */
  retryDunning(orderId: number): Observable<unknown> {
    return this.http.post(
      this.api.buildUrl(`payments/dunning/orders/${orderId}/retry`),
      {},
    );
  }

  /**
   * Fetches the count of orders owned by a user (`GET /orders/owned-count`),
   * powering the board's "My orders" pill. Independent of the board's result cap
   * and other filters, so the count is the true owned-order total.
   *
   * @param userId - The owner's id (the frontend passes the current user's `sub`).
   * @returns `{ count }` — the owned-order total.
   */
  getOwnedCount(userId: number): Observable<OwnedCountResponse> {
    const params = new HttpParams().set("owner", String(userId));
    return this.http.get<OwnedCountResponse>(
      this.api.buildUrl("orders/owned-count"),
      { params },
    );
  }

  getOrderStats(businessUnitIds?: number[]): Observable<OrderStatsResponse> {
    let httpParams = new HttpParams();
    if (businessUnitIds?.length)
      httpParams = httpParams.set("businessUnitIds", businessUnitIds.join(","));
    return this.http.get<OrderStatsResponse>(
      this.api.buildUrl("orders/stats"),
      { params: httpParams },
    );
  }

  getBusinessUnits(): Observable<BusinessUnit[]> {
    return this.http.get<BusinessUnit[]>(
      this.api.buildUrl("orders/business-units"),
    );
  }

  /**
   * Fetches the operator-selectable status vocabulary (`GET /orders/statuses`).
   *
   * Sourced from the dashboard's `IsVisible = 1` statuses via the admin-api
   * `OrderStatusReader` seam (R3 slice 02), so settability reflects the
   * dashboard — not the current filtered board view. Replaces the legacy
   * `stats()`-derived dropdown options.
   *
   * @returns Array of `{ statusId, displayName }` selectable statuses.
   */
  getSelectableStatuses(): Observable<SelectableStatus[]> {
    return this.http.get<SelectableStatus[]>(
      this.api.buildUrl("orders/statuses"),
    );
  }

  updateOrderStatus(orderId: number, orderStatusId: number): Observable<any> {
    return this.http.patch(this.api.buildUrl(`orders/${orderId}/status`), {
      orderStatusId,
    });
  }

  /**
   * Pauses an order's SLA clock (`POST /orders/:id/pause`).
   *
   * Idempotent server-side — re-pausing a paused order is a no-op. One of the
   * four order mutations sharing this service (R3 slice 06).
   *
   * @param orderId - DeedNetwork order id to pause.
   * @returns Server response `{ success, orderId, isPaused, message }`.
   */
  pauseOrder(orderId: number): Observable<any> {
    return this.http.post(this.api.buildUrl(`orders/${orderId}/pause`), {});
  }

  /**
   * Resumes a paused order's SLA clock (`POST /orders/:id/resume`).
   *
   * Idempotent server-side — resuming a non-paused order is a no-op. Folds the
   * in-flight pause segment into `PauseDurationMs` (R3 slice 06).
   *
   * @param orderId - DeedNetwork order id to resume.
   * @returns Server response `{ success, orderId, isPaused, pauseDurationMs, message }`.
   */
  resumeOrder(orderId: number): Observable<any> {
    return this.http.post(this.api.buildUrl(`orders/${orderId}/resume`), {});
  }

  /**
   * Toggles an order's completion flag and aligns its dashboard status server-side.
   *
   * @param orderId - DeedNetwork order id.
   * @param isCompleted - Target completion flag.
   * @returns Server response with applied `isCompleted` and resulting `orderStatusId`.
   */
  updateOrderCompletion(
    orderId: number,
    isCompleted: boolean,
  ): Observable<any> {
    return this.http.patch(this.api.buildUrl(`orders/${orderId}/completion`), {
      isCompleted,
    });
  }

  /**
   * Assigns, reassigns, or clears an order's owner via the tri-state
   * `PATCH /orders/:id/admin-fields` endpoint (R4).
   *
   * @param orderId - DeedNetwork order id to (re)assign.
   * @param userId - Owner id to set, or `null` to clear the owner. The `null`
   *   is sent explicitly (not omitted) so the backend's tri-state clear fires.
   * @returns Server response `{ success, orderId, message }`.
   */
  assignOrder(orderId: number, userId: number | null): Observable<any> {
    return this.http.patch(
      this.api.buildUrl(`orders/${orderId}/admin-fields`),
      {
        assignedToUserId: userId,
      },
    );
  }

  /**
   * Returns the active internal admins an order may be assigned to
   * (`GET /orders/assignable-users`).
   *
   * The underlying request is `shareReplay(1)`-cached, so all board cards share
   * a single fetch for the lifetime of the service — opening one card's popover
   * fetches the list, and every subsequent popover replays the cached value
   * without a new HTTP request.
   *
   * @returns Cached, replayed observable of `{ userId, name }` assignable users.
   */
  getAssignableUsers(): Observable<AssignableUser[]> {
    this.assignableUsers$ ??= this.http
      .get<AssignableUser[]>(this.api.buildUrl("orders/assignable-users"))
      .pipe(shareReplay(1));
    return this.assignableUsers$;
  }
}
